/**
 * Account page — the authenticated home of the portal.
 *
 * Renders three branches from a single `/me` call:
 *   - happy path:        ok + install_verified_at set
 *                        → activation date row
 *   - pending first use: ok + install_verified_at null
 *                        → "Pending first use" message
 *   - not onboarded:     not_onboarded (no customer_chat_tenants row)
 *                        → static "your company isn't on Finn yet" screen
 *
 * Two variants exist because `useMsal` can only be called inside an
 * MsalProvider, which is mounted only when the portal AAD app is
 * configured. The auth-disabled local-dev path skips MSAL entirely
 * and lets the engine's bypass authenticate the request.
 */
import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { acquireIdToken } from "@/auth/msal";
import { getMe, type MeResult } from "@/api/portal";
import { Card, ContactLine, Icon, Spinner } from "@/components/ui";
import { useI18n } from "@/lib/i18n";

function formatActivationDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 px-4 py-3.5">
      <dt className="text-body-sm text-on-surface-variant">{label}</dt>
      <dd className="text-right text-body-sm font-medium text-on-surface">
        {value || "—"}
      </dd>
    </div>
  );
}

function NotOnboardedScreen() {
  const { t } = useI18n();
  return (
    <div className="rounded-xl border border-outline-variant bg-surface-container p-6">
      <div className="flex gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-surface-container-highest text-on-surface-variant">
          <Icon name="apartment" />
        </span>
        <div>
          <h2 className="text-section-head text-on-surface">
            {t("notOnboarded.title")}
          </h2>
          <p className="mt-2 text-body-sm text-on-surface-variant">
            {t("notOnboarded.body")}
          </p>
          <ContactLine />
        </div>
      </div>
    </div>
  );
}

function AccountView({ me }: { me: MeResult }) {
  const { t } = useI18n();
  const activation = me.install_verified_at
    ? formatActivationDate(me.install_verified_at)
    : t("account.pendingFirstUse.label");
  const identity = me.signed_in_name
    ? `${me.signed_in_name}${me.signed_in_email ? ` (${me.signed_in_email})` : ""}`
    : me.signed_in_email ?? "—";
  return (
    <div className="space-y-8">
      <h1 className="text-page-title text-on-surface">{t("account.title")}</h1>

      <section>
        <h2 className="mb-3 text-section-head text-on-surface">
          {t("account.sectionTitle")}
        </h2>
        <dl className="divide-y divide-outline-variant rounded-xl border border-outline-variant bg-surface-container-lowest">
          <DetailRow label={t("account.activatedOn")} value={activation} />
          <DetailRow
            label={t("account.company")}
            value={me.customer_display_name ?? ""}
          />
          <DetailRow label={t("account.signedInAs")} value={identity} />
        </dl>
        {!me.install_verified_at && (
          <p className="mt-4 text-body-sm text-on-surface-variant">
            {t("account.pendingFirstUse.body")}
          </p>
        )}
      </section>
    </div>
  );
}

function AccountFrame({ fetchMe }: { fetchMe: () => Promise<MeResult> }) {
  const { t } = useI18n();
  const [me, setMe] = useState<MeResult | null>(null);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    setError(false);
    fetchMe()
      .then((result) => {
        if (!cancelled) setMe(result);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchMe]);

  if (error) {
    return (
      <Card>
        <p className="text-body-sm text-on-surface-variant">{t("error.body")}</p>
      </Card>
    );
  }
  if (!me) {
    return (
      <Card>
        <Spinner />
        <p className="mt-4 text-center text-body-sm text-on-surface-variant">
          {t("account.loading")}
        </p>
      </Card>
    );
  }
  if (me.result === "not_onboarded") {
    return <NotOnboardedScreen />;
  }
  return <AccountView me={me} />;
}

function AccountPageAuthed() {
  const { instance, accounts } = useMsal();
  const fetchMe = async () => {
    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) {
      // Phase 3 will add a proper signed-out gate; for Phase 1 the
      // route assumes the user is signed in (the wizard tail lands
      // them here, or the future landing CTA does).
      throw new Error("not signed in");
    }
    const idToken = await acquireIdToken(instance, account);
    return getMe(idToken);
  };
  return <AccountFrame fetchMe={fetchMe} />;
}

function AccountPageNoAuth() {
  // FINN_PORTAL_AUTH_DISABLED — engine accepts the request without a
  // Bearer header and stamps the dev-stub identity.
  return <AccountFrame fetchMe={() => getMe(null)} />;
}

export function AccountPage({ authConfigured }: { authConfigured: boolean }) {
  return authConfigured ? <AccountPageAuthed /> : <AccountPageNoAuth />;
}
